/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {
  Entity
} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, noneOption} from '../components/menus/Dropdown';

/**
 * Defines the prop definitions for EngineTypeChooser,
 *
 * required :         Indicates field is required or not.
 * readOnly :         Indicates whether the dropdown is read-only mode.
 * value :            value from parent component to control this component from out.
 * defaultValue :     Default value to display when intially renders.
 * onChange :         The callback to be called when the value changes.
 * includeNone :      The flag indicates whether to add onone option into the drop down options.
 * includeNoneLabel : None option label
 */
export interface EngineTypeChooserProps {
  entity?: Entity;
  type?: string,
  readOnly?: boolean;
  required?: boolean;
  onChange?: Function;
  value?: string;
  includeNone?: boolean;
  includeNoneLabel?: string;
  defaultValue?: string;
  includeDeleted?: boolean;
};

export const getEngineTypeOptions = (includeNone = false, includeNoneLabel?: string) => {
  const timeFormatOptions = translate('common:editors.engineType.options', {returnObjects: true});
  const options = createOptions({
    items     : timeFormatOptions,
    labelProp : 'label',
    valProp   : 'value'
  });
  return noneOption({
    label : includeNoneLabel,
    value : 'none',
    includeNone,
    options
  });
};
/**
 * Defines the EngineTypeChooser component. The EngineTypeChooser provides a list of engine types to
 * display within a dropdown list and allows the user to select a engine type from the list
 * which will be returned to the  client as the selected engine type.
 *
 * ```typescript
 *
 *  import EngineTypeChooser from '@ion/dls/src/editors/';
 *
 *  <EngineTypeChooser readOnly={isViewDialog}
 *     defaultValue={formState[AssetFormFields.FuelType]?.value}
 *     onChange={function(value) {
 *         handleChooserChange({
 *           name : AssetFormFields.FuelType,
 *          value
 *         });
 *     }} />
 *
 */
export const EngineTypeChooser = ({
  defaultValue,
  onChange,
  value,
  required = false,
  readOnly = false,
  includeNone = false,
  includeNoneLabel = translate('common:options.none')
}: EngineTypeChooserProps) => {
  const [engineType, setEngineType] = useState(null);
  const options             = getEngineTypeOptions(includeNone, includeNoneLabel);

  useEffect(() => {
    !!value && setEngineType(value);
  }, [value]);

  useEffect(() => {
    const defValue  = options.find(({value}) => defaultValue === value)?.value;
    if (!isUndefined(defValue)) {
      onChange?.(defValue);
      setEngineType(defValue);
    }
  }, []);

  return (
    <Dropdown required={required}
      labelAsValue={false}
      displaySelectedLabel
      disabled={readOnly}
      value={engineType}
      options={options}
      placeholder={translate('common:editors.engineType.placeholder')}
      fieldLabel={translate('common:editors.engineType.label')}
      onChange={function({target: {value}}) {
        setEngineType(value);
        onChange?.(value);
      }}/>
  );
};
export default EngineTypeChooser;
